// ============================================================================
// TabBar — ЕДИНЫЙ механизм вкладок платформы (SRP/DRY).
// Segmented-pill стиль (как Linear/Chrome-вкладки): фон muted, активная
// вкладка — карточная подложка с плавным перетеканием (framer-motion
// layoutId). Компактный, применим везде: формы, панели свойств, страницы,
// desktop и мобильный (overflow-x-auto + скрытый скролл — при нехватке
// ширины вкладки листаются горизонтально).
// НИКАКИХ рукописных кнопок вкладок — только этот компонент.
// ============================================================================

import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export interface TabItem {
  id: string;
  label: React.ReactNode;
}

interface TabBarProps {
  tabs: TabItem[];
  active: string;
  onChange: (id: string) => void;
  className?: string;
  /** уникальный префикс анимации — обязателен при нескольких TabBar на экране */
  group?: string;
  /** Показать аккуратный горизонтальный скроллбар (по умолчанию скрыт) */
  showScrollbar?: boolean;
}

export function TabBar({ tabs, active, onChange, className, group = 'tab', showScrollbar = false }: TabBarProps) {
  // Активная вкладка всегда должна быть видна целиком: при нехватке ширины
  // ряд прокручивается ТОЛЬКО по горизонтали, активная подкручивается в вид.
  const activeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [active]);

  return (
    <div
      role="tablist"
      className={cn(
        // Только ГОРИЗОНТАЛЬНАЯ прокрутка: overflow-x-auto неявно делает
        // overflow-y:auto, из-за чего в ряду появлялся вертикальный скроллбар
        // и обрезалась последняя вкладка — overflow-y-hidden фиксирует ось.
        'inline-flex max-w-full items-center gap-0.5 overflow-x-auto overflow-y-hidden overscroll-x-contain rounded-lg bg-muted p-0.5',
        showScrollbar
          ? '[scrollbar-width:thin] [scrollbar-color:var(--color-border)_transparent] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border'
          : '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      {tabs.map((t) => {
        const isActive = t.id === active;
        return (
          <button
            key={t.id}
            ref={isActive ? activeRef : undefined}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.id)}
            className={cn(
              // shrink-0 — вкладка не сжимается ниже текста (иначе «Оформление»
              // обрезалось); при нехватке ширины ряд листается горизонтально.
              'relative shrink-0 whitespace-nowrap rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors',
              isActive
                ? 'text-primary' // надпись активного таба — цвет темы (primary)
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/60',
            )}
          >
            {isActive && (
              <motion.span
                layoutId={`tabbar-${group}`}
                // Рамка активного таба — цвет темы (primary/акцент)
                className="absolute inset-0 rounded-md bg-primary/10 ring-1 ring-primary/40"
                transition={{ type: 'spring', stiffness: 500, damping: 40 }}
              />
            )}
            <span className="relative z-10">{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}